/**
 * Module-level cache: userId string → { label, image?, zoomStatus? }.
 *
 * Populated by `useInterviewerField.loadOptions` whenever the async
 * select fetches interviewers.  Read by `useEditConfirmation.requestSave`
 * so newly searched+selected interviewers (whose metadata is NOT in the
 * initial `participant_default_options`) still appear in the confirmation
 * dialog with correct name/avatar.
 *
 * Zoom hosting fields are cached alongside so the Host field's
 * Paid/Busy/Needs-license tags + disabled-option logic can resolve a
 * picked interviewer's status without a refetch (sch-28 host parity).
 */

export interface CachedInterviewerZoomStatus {
  can_host_zoom_meeting?: boolean;
  zoom_rate_limit_reached?: boolean;
  zoom_plan?: number;
  zoom_plan_type?: string;
}

interface CachedOption {
  label: string;
  image?: string;
  zoomStatus?: CachedInterviewerZoomStatus;
  /** IANA timezone or empty when the user has none set. Lets the
   *  save-time validator detect the missing-timezone case for picks
   *  that aren't in the seeded `participant_default_options` (i.e.
   *  interviewers the recruiter added via AsyncSelect in this session). */
  timezone?: string;
}

const cache = new Map<string, CachedOption>();

export function cacheInterviewerOptions(
  options: Array<{
    value: string;
    label: string;
    image?: string;
    zoomStatus?: CachedInterviewerZoomStatus;
    data?: { timezone?: string };
  }>
): void {
  for (const o of options) {
    if (!o.value) continue;
    const entry: CachedOption = {
      label: o.label,
      image: o.image,
      zoomStatus: o.zoomStatus,
    };
    if (o.data?.timezone !== undefined) {
      entry.timezone = o.data.timezone;
    }
    cache.set(o.value, entry);
  }
}

export function getCachedInterviewerOption(
  id: string
): CachedOption | undefined {
  return cache.get(id);
}

/**
 * Patch the cached timezone for an interviewer without touching the
 * other fields. Called from the timezone-fix modal so a later
 * save-time validation sees the updated value (the modal also updates
 * `participant_default_options`, but the cache is the canonical
 * source for picks the user added via AsyncSelect mid-session and
 * which don't live in the seed).
 */
export function setCachedInterviewerTimezone(id: string, tz: string): void {
  const existing = cache.get(id);
  if (existing) {
    cache.set(id, { ...existing, timezone: tz });
  } else {
    cache.set(id, { label: '', timezone: tz });
  }
}
